import { DefaultTeamLogo, useIntl } from '@capsicum/common';
import { Avatar, Label, useRouter } from '@capsicum/common-web';
import { BindingIcon, CalendarIcon, CappedIcon, CheckIcon } from '@capsicum/common-web/icons';
import { type SurveyCardData } from '@capsicum/features/survey/shared';
import styled, { type DefaultTheme } from 'styled-components';

export function SurveyListCard({
	hasVoted,
	isBound,
	isSurveyClosed,
	isSurveyScheduled,
	labelProps,
	mainText,
	partnerId,
	surveyId,
	teamLogoUri,
	teamName,
	teamNameShort,
	testID,
	variant = 'listCard'
}: SurveyCardData) {
	const { push } = useRouter();
	const { $t } = useIntl();

	const voteStatus = () => {
		if (hasVoted) {
			return $t('survey.voted');
		}

		if (isSurveyClosed) {
			return $t('survey.didNotVote');
		}

		if (isSurveyScheduled) {
			return $t('survey.pollNotOpenedYet');
		}

		return $t('survey.notYetVoted');
	};

	const voteActions = () => {
		if (isSurveyClosed || hasVoted) {
			return $t('survey.viewResults');
		}

		if (isSurveyScheduled) {
			return $t('survey.previewPoll');
		}

		return $t('survey.voteNow');
	};

	const navigateToSurveyDetails = () => push(`/votes/${partnerId}/${surveyId}`);

	return (
		<S.CardContainer data-testid={testID} onClick={navigateToSurveyDetails}>
			<S.TeamNameLabelsContainer>
				{variant === 'listCard' && (
					<S.TeamLogoContainer>
						<Avatar size="XS" src={teamLogoUri || DefaultTeamLogo} onClick={navigateToSurveyDetails} />

						<S.TeamNameContainer>
							<S.TeamNameShort>{teamNameShort}</S.TeamNameShort>
							<S.TeamName>{teamName}</S.TeamName>
						</S.TeamNameContainer>
					</S.TeamLogoContainer>
				)}
				<S.LabelContainer>
					<Label icon={CalendarIcon} label={labelProps.labelText} variant={labelProps.color} />
					<Label icon={CappedIcon} variant="blue" />
					{isBound && <Label icon={BindingIcon} variant="blue" />}
				</S.LabelContainer>
			</S.TeamNameLabelsContainer>

			<S.MainTextContainer>
				<S.MainText>{mainText}</S.MainText>
			</S.MainTextContainer>

			<S.VoteStatusContainer>
				<S.BottomText>
					<S.CheckIconContainer $hasVoted={hasVoted}>
						<S.Check $hasVoted={hasVoted} />
					</S.CheckIconContainer>
					<S.VoteStatus>{voteStatus()}</S.VoteStatus>
				</S.BottomText>
				<S.BottomText>
					<S.VoteAction>{voteActions()}</S.VoteAction>
				</S.BottomText>
			</S.VoteStatusContainer>
		</S.CardContainer>
	);
}

namespace S {
	export const CardContainer = styled.div`
		display: flex;
		flex-direction: column;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		width: 100%;
		${({ theme }) => theme.border.radius.medium}
		padding:${({ theme }) => theme.spacings.small};
		cursor: pointer;
	`;

	export const TeamNameLabelsContainer = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
		flex-direction: row;
		display: flex;
		justify-content: space-between;
		width: 100%;
	`;
	export const TeamLogoContainer = styled.div`
		display: flex;
		flex-direction: row;
		flex: 1;
		min-width: 0;
	`;
	export const Logo = styled.img`
		width: 36.85px;
		height: 36.85px;
		border-radius: 50%;
	`;
	export const TeamNameContainer = styled.div`
		margin-left: ${({ theme }) => theme.spacings.xxsmall};
		flex: 1;
		min-width: 0;
	`;
	export const TeamNameShort = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text1}
	`;
	export const TeamName = styled.span`
		display: block;
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	`;

	export const MainTextContainer = styled.div`
		padding: ${({ theme }) => theme.spacings.xxsmall};
		background: ${({ theme }) => theme.colors.borders.main.divider};
		${({ theme }) => theme.border.radius.medium}
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const MainText = styled.span`
		${({ theme }) => theme.fonts.text.text6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const LabelContainer = styled.div`
		flex-direction: row;
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		height: 24px;
		flex-shrink: 0;
	`;
	export const CheckIconContainer = styled.div<{
		$hasVoted: boolean;
	}>`
		${({ theme }) => theme.border.radius.circle};
		border: ${({ theme }) => theme.border.width.medium} solid
			${({ $hasVoted, theme }) =>
				$hasVoted ? theme.colors.borders.brand.enabled : theme.colors.borders.main.unselected};
		height: 20px;
		width: 20px;
		align-items: center;
		display: flex;
		flex-direction: row;
		justify-content: center;
	`;

	export const Check = styled(CheckIcon)<{
		$hasVoted: boolean;
		theme: DefaultTheme;
	}>`
		fill: ${({ $hasVoted, theme }) =>
			$hasVoted ? theme.colors.borders.brand.enabled : theme.colors.borders.main.unselected};
		color: ${({ $hasVoted, theme }) =>
			$hasVoted ? theme.colors.borders.brand.enabled : theme.colors.borders.main.unselected};
		height: 12px;
		width: 12px;
	`;

	export const VoteStatusContainer = styled.div`
		margin-top: auto;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		row-gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const VoteStatus = styled.span`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.subtle};
		margin: 0 ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const VoteAction = styled.span`
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const BottomText = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
	`;
}
